import { useRegisterBusiness } from '../hooks/useRegisterBusiness.js';
import { Loader, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import LocationPickerModal from '../components/form/LocationPickerModal';
import HoursModal from '../components/form/HoursModal';
import DynamicFormRenderer from '../components/form/DynamicFormRenderer';

const RegisterBusinessDynamicV2 = ({ userData }) => {
  const vm = useRegisterBusiness(userData);
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    form: true,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle dynamic form field changes
  const handleDynamicFieldChange = (slug, value) => {
    // Handle nested mapAddress fields
    if (slug === 'mapAddress') {
      vm.setFormData(prev => ({ ...prev, mapAddress: value }));
    } else {
      vm.setFormData(prev => ({ ...prev, [slug]: value }));
    }
  };

  // Handle dynamic image uploads - same as EditBusiness
  const handleDynamicImageUpload = async (e, slug, isMultiple) => {
    const files = isMultiple ? Array.from(e.target.files || []) : [e.target.files?.[0]];
    if (!files.length || !files[0]) return;

    try {
      if (isMultiple) {
        // Upload multiple files
        const uploadedUrls = await vm.uploadMultipleFiles(files);
        vm.setFormData(prev => ({
          ...prev,
          [slug]: [...(Array.isArray(prev[slug]) ? prev[slug] : []), ...uploadedUrls]
        }));
      } else {
        // Upload single file
        const uploadedUrl = await vm.uploadSingleFile(files[0]);
        vm.setFormData(prev => ({ ...prev, [slug]: uploadedUrl }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      vm.setError(err.message || 'Failed to upload image');
    }
  };

  if (vm.categoriesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Sticky Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-emerald-500/20 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => vm.navigate('/dashboard?tab=my-businesses')}
              className="text-slate-400 hover:text-emerald-400 transition"
            >
              ← Back
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-white">
                Register {vm.selectedMainCategory || 'Entity'}
              </h1>
              <p className="text-xs sm:text-sm text-slate-400">
                {vm.formSkeleton?.formSubtitle || 'Complete all sections to register'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-4">

          {/* Alerts */}
          {vm.error && (
            <div className="mb-4 bg-red-50/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-300 text-sm">{vm.error}</p>
            </div>
          )}
          {vm.success && (
            <div className="mb-4 bg-green-50/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <p className="text-green-300 text-sm">{vm.success}</p>
            </div>
          )}

          <form id="register-business-form" onSubmit={vm.handleSubmit} className="space-y-4">

            {/* Category Selection Section */}
            <Section
              title="Select Category"
              section="category"
              expanded={expandedSections.category}
              onToggle={toggleSection}
            >
              <div className="space-y-6">
                {/* Main Category Selection */}
                <div>
                  <h3 className="text-slate-300 font-semibold mb-3 text-sm">Main Category</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {vm.mainCategories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          vm.setSelectedMainCategory(category.name);
                          vm.setSelectedMainCategoryObj(category);
                          vm.setSelectedCategory('');
                          vm.setSelectedSubcategory('');
                        }}
                        className={`p-4 rounded-xl border transition-all ${
                          vm.selectedMainCategory === category.name
                            ? 'bg-emerald-500/20 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                            : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 hover:border-emerald-500/50'
                        }`}
                      >
                        <div className="text-center">
                          {(category.iconUrl || category.icon) && (
                            <div className="text-3xl mb-3 flex items-center justify-center">
                              <img
                                src={category.iconUrl || category.icon}
                                alt={category.name}
                                className="w-10 h-10 object-contain mx-auto"
                              />
                            </div>
                          )}
                          <h4
                            className={`font-semibold text-sm ${
                              vm.selectedMainCategory === category.name ? 'text-emerald-400' : 'text-slate-300'
                            }`}
                          >
                            {category.name}
                          </h4>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Business Types Selection (for Sampada, Tourist Place, Organization) - NOT for Home */}
                {vm.selectedMainCategoryObj && vm.selectedMainCategoryObj.businessTypes && vm.selectedMainCategoryObj.businessTypes.length > 0 && vm.selectedMainCategory !== 'Home' && (
                  <div>
                    <h3 className="text-slate-300 font-semibold mb-3 text-sm">Type</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {vm.selectedMainCategoryObj.businessTypes.map((businessType) => (
                        <button
                          key={businessType.id}
                          type="button"
                          onClick={() => {
                            vm.setSelectedCategory(businessType.id);
                            vm.setSelectedSubcategory('');
                          }}
                          className={`p-4 rounded-xl border transition-all ${
                            vm.selectedCategory === businessType.id
                              ? 'bg-emerald-500/20 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                              : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 hover:border-emerald-500/50'
                          }`}
                        >
                          <div className="text-center">
                            <h4
                              className={`font-semibold text-sm ${
                                vm.selectedCategory === businessType.id ? 'text-emerald-400' : 'text-slate-300'
                              }`}
                            >
                              {businessType.name}
                            </h4>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category Selection - Only show if NO businessTypes */}
                {vm.selectedMainCategory && !vm.selectedMainCategoryObj?.businessTypes?.length && vm.getSubcategoriesForMain(vm.selectedMainCategory).length > 0 && (
                  <div>
                    <h3 className="text-slate-300 font-semibold mb-3 text-sm">Category Type</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {vm.getSubcategoriesForMain(vm.selectedMainCategory).map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => {
                            vm.setSelectedCategory(category.id);
                            vm.setSelectedSubcategory('');
                          }}
                          className={`p-4 rounded-xl border transition-all ${
                            vm.selectedCategory === category.id
                              ? 'bg-emerald-500/20 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                              : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 hover:border-emerald-500/50'
                          }`}
                        >
                          <div className="text-center">
                            <h4
                              className={`font-semibold text-sm ${
                                vm.selectedCategory === category.id ? 'text-emerald-400' : 'text-slate-300'
                              }`}
                            >
                              {category.name}
                            </h4>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subcategory Selection */}
                {vm.selectedCategory && vm.getSubcategoriesForCategory(vm.selectedCategory).length > 0 && (
                  <div>
                    <h3 className="text-slate-300 font-semibold mb-3 text-sm">Subcategory</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {vm.getSubcategoriesForCategory(vm.selectedCategory).map((subcategory) => (
                        <button
                          key={subcategory.id}
                          type="button"
                          onClick={() => vm.setSelectedSubcategory(subcategory.id)}
                          className={`p-4 rounded-xl border transition-all ${
                            vm.selectedSubcategory === subcategory.id
                              ? 'bg-emerald-500/20 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                              : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 hover:border-emerald-500/50'
                          }`}
                        >
                          <div className="text-center">
                            <h4
                              className={`font-semibold text-sm ${
                                vm.selectedSubcategory === subcategory.id ? 'text-emerald-400' : 'text-slate-300'
                              }`}
                            >
                              {subcategory.name}
                            </h4>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* Dynamic Form Section */}
            {vm.selectedMainCategory && (
              <Section
                title={vm.formSkeleton?.formTitle || 'Registration Form'}
                section="form"
                expanded={expandedSections.form}
                onToggle={toggleSection}
              >
                {vm.loadingFormSkeleton ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 text-emerald-400 animate-spin" />
                    <span className="ml-2 text-slate-400">Loading form...</span>
                  </div>
                ) : vm.formSkeleton?.fields ? (
                  <>
                    {/* Show General Information title and description at top */}
                    {(() => {
                      const generalInfoField = vm.formSkeleton.fields.find(f => f.type === 'layout' && f.options?.label === 'General Information');
                      if (generalInfoField) {
                        return (
                          <div className="mb-6">
                            <h3 className="text-slate-300 font-semibold text-sm mb-2">{generalInfoField.options.label}</h3>
                            {generalInfoField.options.hint && (
                              <p className="text-slate-400 text-sm mb-6">{generalInfoField.options.hint}</p>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Check if General Information layout exists */}
                    {(() => {
                      const hasGeneralInfo = vm.formSkeleton.fields.some(f => f.type === 'layout' && f.options?.label === 'General Information');
                      
                      // If no General Information layout, show name/tag at top
                      if (!hasGeneralInfo) {
                        return (
                          <div className="mb-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                              <FormField label={`${vm.selectedMainCategory} Name`} required>
                                <input
                                  type="text"
                                  name="grouped"
                                  value={vm.formData.grouped}
                                  onChange={(e) => handleDynamicFieldChange('grouped', e.target.value)}
                                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                                  placeholder={`Enter ${vm.selectedMainCategory.toLowerCase()} name`}
                                />
                              </FormField>

                              <FormField label="kahaTAG" required>
                                <div className="relative">
                                  <span className="absolute left-4 top-3 text-slate-400 font-semibold">@</span>
                                  <input
                                    type="text"
                                    name="tag"
                                    value={vm.formData.tag}
                                    onChange={(e) => handleDynamicFieldChange('tag', e.target.value.toUpperCase())}
                                    className={`w-full pl-8 pr-4 py-3 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:ring-2 transition uppercase ${vm.tagError ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400/30' : vm.tagSuccess ? 'border-emerald-500/50 focus:border-emerald-400 focus:ring-emerald-400/30' : 'border-slate-700 focus:border-emerald-500 focus:ring-emerald-500/30'}`}
                                    placeholder="KAHATAAG"
                                  />
                                  {vm.checkingTag && <span className="absolute right-4 top-3 text-slate-400"><Loader className="w-4 h-4 animate-spin" /></span>}
                                </div>
                                {vm.tagError && <p className="text-red-400 text-xs mt-2">{vm.tagError}</p>}
                                {vm.tagSuccess && <p className="text-emerald-400 text-xs mt-2">{vm.tagSuccess}</p>}
                              </FormField>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Name and Tag Fields (when General Information exists) */}
                    {(() => {
                      const hasGeneralInfo = vm.formSkeleton.fields.some(f => f.type === 'layout' && f.options?.label === 'General Information');
                      if (hasGeneralInfo) {
                        return (
                          <div className="mb-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                              <FormField label={`${vm.selectedMainCategory} Name`} required>
                                <input
                                  type="text"
                                  name="grouped"
                                  value={vm.formData.grouped}
                                  onChange={(e) => handleDynamicFieldChange('grouped', e.target.value)}
                                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                                  placeholder={`Enter ${vm.selectedMainCategory.toLowerCase()} name`}
                                />
                              </FormField>

                              <FormField label="kahaTAG" required>
                                <div className="relative">
                                  <span className="absolute left-4 top-3 text-slate-400 font-semibold">@</span>
                                  <input
                                    type="text"
                                    name="tag"
                                    value={vm.formData.tag}
                                    onChange={(e) => handleDynamicFieldChange('tag', e.target.value.toUpperCase())}
                                    className={`w-full pl-8 pr-4 py-3 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:ring-2 transition uppercase ${vm.tagError ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400/30' : vm.tagSuccess ? 'border-emerald-500/50 focus:border-emerald-400 focus:ring-emerald-400/30' : 'border-slate-700 focus:border-emerald-500 focus:ring-emerald-500/30'}`}
                                    placeholder="KAHATAAG"
                                  />
                                  {vm.checkingTag && <span className="absolute right-4 top-3 text-slate-400"><Loader className="w-4 h-4 animate-spin" /></span>}
                                </div>
                                {vm.tagError && <p className="text-red-400 text-xs mt-2">{vm.tagError}</p>}
                                {vm.tagSuccess && <p className="text-emerald-400 text-xs mt-2">{vm.tagSuccess}</p>}
                              </FormField>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Render form fields, skipping General Information layout */}
                    {vm.formSkeleton.fields.map((field, index) => {
                      // Skip the General Information layout entirely
                      if (field.type === 'layout' && field.options?.label === 'General Information') {
                        return (
                          <div key={index}>
                            {/* Rest of General Information fields */}
                            <div className={`${field.options.axis === 'v' ? 'space-y-4' : field.options.axis === 's' ? 'space-y-6' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}`}>
                              {field.children && field.children.map((child, idx) => (
                                <DynamicFormRenderer
                                  key={idx}
                                  fields={[child]}
                                  formData={vm.formData}
                                  onChange={handleDynamicFieldChange}
                                  onImageUpload={handleDynamicImageUpload}
                                  onLocationPick={() => vm.setShowLocationPicker(true)}
                                  onHoursEdit={() => vm.setShowHoursModal(true)}
                                  imageRefs={vm.dynamicImageRefs}
                                  uploadingImage={vm.uploadingImage}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      }

                      // Skip nestedselection and entitytypedropdown fields - they're handled in category section
                      if (field.type === 'nestedselection' || field.type === 'entitytypedropdown') {
                        return null;
                      }

                      // For other fields, render normally
                      return (
                        <DynamicFormRenderer
                          key={index}
                          fields={[field]}
                          formData={vm.formData}
                          onChange={handleDynamicFieldChange}
                          onImageUpload={handleDynamicImageUpload}
                          onLocationPick={() => vm.setShowLocationPicker(true)}
                          onHoursEdit={() => vm.setShowHoursModal(true)}
                          imageRefs={vm.dynamicImageRefs}
                          uploadingImage={vm.uploadingImage}
                        />
                      );
                    })}
                  </>
                ) : (
                  <div className="text-slate-400 text-center py-8">
                    <p>No form template available for this category.</p>
                    <p className="text-sm mt-2">Please select a different category or contact support.</p>
                  </div>
                )}
              </Section>
            )}

            {/* Footer Actions */}
            {vm.selectedMainCategory && (
              <div className="flex gap-3 pt-6 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => vm.navigate('/dashboard?tab=my-businesses')}
                  className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={vm.loading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-emerald-500/50 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {vm.loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Registering...
                    </>
                  ) : (
                    `Register ${vm.selectedMainCategory}`
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Modals */}
      {vm.showLocationPicker && (
        <LocationPickerModal
          formData={vm.formData}
          mapCenter={vm.mapCenter}
          searchQuery={vm.searchQuery}
          onSearchChange={vm.handleSearchLocation}
          onMapClick={(lat, lng) => vm.getLocationFromCoordinates(lat, lng)}
          onCoordinateChange={(key, val) => {
            vm.setFormData(prev => ({ ...prev, [key]: val }));
          }}
          onAddressChange={(key, val) => {
            vm.setFormData(prev => ({ ...prev, mapAddress: { ...prev.mapAddress, [key]: val } }));
          }}
          getLocationFromCoordinates={vm.getLocationFromCoordinates}
          onClose={() => {
            vm.setShowLocationPicker(false);
          }}
        />
      )}
      {vm.showHoursModal && (
        <HoursModal
          workingDaysAndHours={vm.formData.workingDaysAndHours}
          onHoursChange={vm.handleHoursChange}
          onApplyAll={vm.applyHoursToAllDays}
          onClose={() => vm.setShowHoursModal(false)}
        />
      )}
    </div>
  );
};

const Section = ({ title, section, expanded, onToggle, children }) => (
  <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden shadow-lg hover:shadow-emerald-500/10 transition">
    <button
      type="button"
      onClick={() => onToggle(section)}
      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-800/60 transition"
    >
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full" />
        <h2 className="text-lg font-semibold text-white">{title}</h2>
      </div>
      <ChevronDown
        className={`w-5 h-5 text-slate-400 transition transform ${expanded ? 'rotate-180' : ''}`}
      />
    </button>
    {expanded && (
      <div className="px-6 py-4 border-t border-slate-700/50 space-y-4">
        {children}
      </div>
    )}
  </div>
);

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-slate-300 font-medium mb-2 text-sm">
      {label}
      {required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default RegisterBusinessDynamicV2;
